import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Alerte, Bouton, Carte, Champ } from '../ui';
import { ChampMotDePasse } from '../ui/ChampMotDePasse';
import { FournisseursTiers } from '../auth/Fournisseurs';
import { EcranEntree } from '../auth/EcranEntree';
import { messageFournisseur } from '../auth/echecFournisseur';
import { messageDErreur, useSession } from '../auth/session';
import { useTraduction } from '../i18n/langue';

/**
 * Écran de connexion.
 *
 * La suite de la connexion est confiée à l'appelant (`surConnexion`) plutôt qu'à une
 * garde de route rejouée après coup : c'est explicite, et cela laisse l'écran montable
 * seul en test, hors routeur.
 */
export function Connexion({
  surConnexion,
  surInscription,
  surMotDePasseOublie,
  reprise,
  echecFournisseur,
}: {
  surConnexion?: () => void;
  surInscription?: () => void;
  surMotDePasseOublie?: () => void;
  /** Code de reprise rapporté par le fournisseur d'identité (T-041). */
  reprise?: string;
  /** Motif d'échec rapporté par le fournisseur, s'il y en a un. */
  echecFournisseur?: string;
} = {}) {
  const { connecter, reprendreConnexion } = useSession();
  const { messages } = useTraduction();
  const m = messages.entree.connexion;
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(
    echecFournisseur !== undefined ? messageFournisseur(echecFournisseur, m) : null,
  );
  const [envoi, setEnvoi] = useState(false);
  // Le code de reprise ne vaut qu'une fois : un rendu de plus ne doit pas le rejouer, et
  // le second essai se solderait par un « code déjà utilisé » incompréhensible.
  const repriseTentee = useRef(false);

  useEffect(() => {
    if (reprise === undefined || reprise === '' || repriseTentee.current) return;
    repriseTentee.current = true;
    setEnvoi(true);
    void reprendreConnexion(reprise)
      .then(() => surConnexion?.())
      .catch((cause: unknown) => setErreur(messageDErreur(cause)))
      .finally(() => setEnvoi(false));
  }, [reprise, reprendreConnexion, surConnexion]);

  async function soumettre(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      await connecter(email, motDePasse);
      surConnexion?.();
    } catch (cause) {
      setErreur(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <EcranEntree
      titre={m.titre}
      variante="connexion"
      alternative={
        <>
          <p>{m.pasDeCompte}</p>
          <Bouton variante="lien" onClick={() => surInscription?.()}>
            {m.sInscrire}
          </Bouton>
        </>
      }
    >
      <Carte>
        <form onSubmit={(e) => void soumettre(e)} noValidate>
          <Champ
            label={m.email}
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <ChampMotDePasse
            label={m.motDePasse}
            name="password"
            autoComplete="current-password"
            required
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
          />

          {/* La sortie la plus utile de cet écran, et la seule qui manquait : le serveur
              savait réinitialiser un mot de passe depuis T-013, mais aucune page n'y
              menait. */}
          <p className="lien-oubli">
            <Bouton variante="lien" onClick={() => surMotDePasseOublie?.()}>
              {m.motDePasseOublie}
            </Bouton>
          </p>

          {erreur ? <Alerte titre={m.erreurTitre}>{erreur}</Alerte> : null}

          {/* Pleine largeur : c'est l'unique action de l'écran, et une cible de 160 px
              centrée dans une carte de 484 px sous-emploie la place disponible. */}
          <Bouton type="submit" className="bouton-large" disabled={envoi}>
            {envoi ? m.connexionEnCours : m.verbe}
          </Bouton>
        </form>

        <FournisseursTiers action="connexion" surSession={() => surConnexion?.()} />
      </Carte>
    </EcranEntree>
  );
}
